import React, { useMemo } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useCaeAuditorReliance } from '../../../hooks/analytics/useCaeReports.ts';
import { RefreshMeta, StatCard, StatusChip } from '../controller/shared.tsx';
import type { CAEAuditorRelianceRow } from '../../../request/analytics/caeReports.ts';

/** Report #15 — External Auditor Reliance. Table + reliance count KPI. */
export const AuditorRelianceTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeAuditorReliance();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  const columns = useMemo<TableColumnDef<CAEAuditorRelianceRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control ID',
        header: () => 'Control ID',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 14,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 40,
      },
      {
        id: 'significance',
        columnLabel: 'Significance',
        header: () => 'Significance',
        cell: ({ row }) => <span className="capitalize">{row.original.significance ?? '—'}</span>,
        widthPercent: 16,
      },
      {
        id: 'status',
        columnLabel: 'Test Status',
        header: () => 'Test Status',
        cell: ({ row }) => <StatusChip value={row.original.overall_status ?? row.original.status} />,
        widthPercent: 30,
      },
    ],
    []
  );

  return (
    <div>
      <div className="mb-4">
        <StatCard
          title="Reliance Controls"
          value={rows.length}
          icon={<ShieldCheck className="h-5 w-5" />}
          color="bg-blue-100 text-blue-600"
          subtitle="Controls flagged for external auditor reliance"
        />
      </div>
      <AppDataTable<CAEAuditorRelianceRow>
        data={rows}
        columns={columns}
        loading={isLoading}
        error={isError ? error?.message ?? 'Failed to load auditor reliance data.' : null}
        emptyMessage="No controls flagged for external auditor reliance."
        searchable
        searchPlaceholder="Search controls..."
        paginated
        defaultPageSize={10}
        showPageSizeSelector
        headerStart={
          <div className="flex w-full justify-end">
            <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
          </div>
        }
      />
    </div>
  );
};

export default AuditorRelianceTable;
